/**
 * Flatten nested application payloads, apply CPCFA/OCR/formData overlays, field ordering, and save-path formData builders.
 */
import {
  ACTION_BUSINESS_DETAILS_KEY,
  BIZDATA_LOAN_ADDRESS_LINE1_KEY,
  BIZDATA_LOAN_CITY_KEY,
  BIZDATA_LOAN_COUNTY_KEY,
  BIZDATA_LOAN_ZIP_KEY,
  BUSINESS_SECTION_BUSINESSDATA_KEYS,
  DETAIL_LENDER_LOAN_NUMBER_KEY,
  DISPLAY_ADDRESS_LINE2_KEY,
  DISPLAY_BIZDATA_VETERAN_OWNED_KEY,
  DISPLAY_BORROWER_NAME_KEY,
  DISPLAY_BUSINESS_DBA_KEY,
  DISPLAY_BUSINESS_START_YEAR_KEY,
  DISPLAY_LENDER_CALCAP_ID_KEY,
  DISPLAY_LENDER_CONTACT_EMAIL_KEY,
  DISPLAY_LENDER_CONTACT_PHONE_KEY,
  DISPLAY_LENDER_NAME_KEY,
  WIRE_FRAME_BIZDATA_EXTRA_KEYS,
  WIRE_FRAME_BUSINESS_EXTRA_KEYS,
  WIRE_FRAME_LENDER_DISPLAY_KEYS,
  WIRE_FRAME_SECTION_ORDER,
} from './applicationDetailsWireframeKeys';
import type { ApplicationDetailsProgramKind } from './applicationDetailsProgram';

export type ApplicationDetails = Record<string, any>;

export const AMOUNT_FIELDS = new Set([
  'askedAmount', 'approvedAmount', 'disbursementAmount',
  'borrowerEquity',
  'premiumAmount',
  'borrowerPremiumAmount',
  'calcapPremiumAmount',
  'calcap',
  'calcapAmount',
  'formData_averageAnnualRevenueLast3Years',
  'businessData_annualRevenue',
  'formData_remainBalanceRestructure',
  'formData_amountIncreaseRequested',
  'totalOriginationCharges',
  'secondLenderLoanAmount',
]);

/** CalCAP (Capital Access): these DB columns hold percent points (e.g. 1.75), not dollars. */
export const CALCAP_PERCENT_POINT_FIELDS = new Set([
  'borrowerEquity',
  'borrowerPremiumAmount',
  'calcap',
  'calcapAmount',
]);

export const CALCAP_FINANCIAL_FIELD_LABELS: Record<string, string> = {
  /** Enrollment share (e.g. 100%). */
  borrowerEquity: '% of Loan Enrolled in CalCAP',
  borrowerPremiumAmount: 'Borrower Percentage (%)',
  calcap: 'Lender Percentage (%)',
  /** Borrower% + lender% (or 2× one side). */
  calcapAmount: 'CalCAP Combined Rate (%)',
  calcapPercentage: 'Borrower Fee ($)',
  calcapPremiumAmount: 'Lender Fee ($)',
};

type CalcapOcrFinancialFormRow = {
  formDataKeys: string[];
  applicationField:
    | 'borrowerEquity'
    | 'borrowerPremiumAmount'
    | 'calcap'
    | 'calcapAmount'
    | 'calcapPercentage'
    | 'calcapPremiumAmount'
    | 'totalOriginationCharges';
  valueKind: 'currency' | 'percentPoints';
};

const CALCAP_OCR_FORMDATA_TO_APPLICATION_FINANCIAL: CalcapOcrFinancialFormRow[] = [
  {
    formDataKeys: ['loaninformation_percentage_of_loan_enrolled_in_calcap'],
    applicationField: 'borrowerEquity',
    valueKind: 'percentPoints',
  },
  {
    formDataKeys: ['calcapborroweragreement_borrower_percentage', 'loaninformation_borrower_percentage'],
    applicationField: 'borrowerPremiumAmount',
    valueKind: 'percentPoints',
  },
  {
    formDataKeys: [
      'loaninformation_lender_percentage',
      'calcapborroweragreement_lender_percentage',
      'calcapborroweragreement_lender-percentage',
    ],
    applicationField: 'calcap',
    valueKind: 'percentPoints',
  },
  {
    formDataKeys: ['calcapborroweragreement_total_percentage_paid_to_calcap'],
    applicationField: 'calcapAmount',
    valueKind: 'percentPoints',
  },
  {
    formDataKeys: ['loaninformation_borrower_fee', 'calcapborroweragreement_borrower_fee'],
    applicationField: 'calcapPercentage',
    valueKind: 'currency',
  },
  {
    formDataKeys: ['loaninformation_lender_fee', 'calcapborroweragreement_lender_fee'],
    applicationField: 'calcapPremiumAmount',
    valueKind: 'currency',
  },
  {
    formDataKeys: ['loaninformation_total_loan_origination_charge'],
    applicationField: 'totalOriginationCharges',
    valueKind: 'currency',
  },
];

export const PERCENTAGE_FIELDS = new Set([
  'calcapPercentage',
  'otherProgramPercentage',
  'loanAPR',
  'interestRate',
]);

/** DB may store rate as 0–1 or as percent points; show/edit as percent with two decimal places. */
export const formatStoredPercentForDisplay = (stored: unknown): string => {
  if (stored === null || stored === undefined || stored === '') return '';
  const num = Number(stored);
  if (isNaN(num)) return String(stored);
  const pct = num > 0 && num <= 1 ? num * 100 : num;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(pct);
};

/** CalCAP percent-point columns: stored API values are percent points; display with two decimals. */
export const formatCalcapPercentPointsForDisplay = (stored: unknown): string => {
  if (stored === null || stored === undefined || stored === '') return '';
  const num = Number(stored);
  if (isNaN(num)) return String(stored);
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

export const getFormDataAsRecord = (formData: unknown): Record<string, unknown> | null => {
  if (formData == null) return null;
  if (typeof formData === 'string') {
    try {
      const parsed = JSON.parse(formData) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
      return null;
    } catch {
      return null;
    }
  }
  if (typeof formData !== 'object' || Array.isArray(formData)) return null;
  return formData as Record<string, unknown>;
};

const normalizeFormDataLeafValue = (v: unknown): unknown => {
  if (v === null || v === undefined) return undefined;
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return v;
  if (Array.isArray(v) && v.length > 0) {
    if (v.every((x) => typeof x === 'string')) {
      return v.filter(Boolean).join(';');
    }
    const first = v[0] as { Value?: unknown };
    if (first && typeof first === 'object' && 'Value' in first && first.Value !== undefined) {
      return first.Value;
    }
  }
  if (typeof v === 'object' && v !== null && 'value' in (v as Record<string, unknown>)) {
    return (v as { value: unknown }).value;
  }
  return v;
};

const parseNumericFromFormDataLeaf = (raw: unknown): number | null => {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
  const s = String(raw).replace(/[$,\s%]/g, '');
  if (s === '') return null;
  const n = parseFloat(s);
  return Number.isNaN(n) ? null : n;
};

const parseCalcapPercentPointsFromFormData = (raw: unknown): number | null => {
  const n = parseNumericFromFormDataLeaf(raw);
  if (n === null) return null;
  if (n > 0 && n <= 1) return n * 100;
  return n;
};

export const getFirstFormDataValue = (fd: Record<string, unknown>, keys: string[]): unknown => {
  for (const k of keys) {
    const v = normalizeFormDataLeafValue(fd[k]);
    if (v !== null && v !== undefined && v !== '') return v;
  }
  return undefined;
};

const normalizeFormDataDateForInput = (raw: unknown): string | null => {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (s === '') return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d.toISOString().split('T')[0];
  const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (mdy) {
    const mm = mdy[1].padStart(2, '0');
    const dd = mdy[2].padStart(2, '0');
    const yy = mdy[3].length === 2 ? `20${mdy[3]}` : mdy[3];
    return `${yy}-${mm}-${dd}`;
  }
  return null;
};

/** CPCFA / Excel serial dates (e.g. LOA_LENDER_LOAN_DT) → YYYY-MM-DD for Date of Loan. */
const cpcfaBlobDateToIsoInput = (raw: unknown): string | null => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(raw.trim())) {
    return normalizeFormDataDateForInput(raw);
  }
  const n = Number(raw);
  if (Number.isFinite(n) && n >= 1) {
    const excelEpoch = new Date(1899, 11, 30);
    const days = Math.floor(n);
    const fractionalDays = n - days;
    const date = new Date(excelEpoch);
    date.setDate(date.getDate() + days);
    date.setTime(date.getTime() + Math.round(fractionalDays * 86400000));
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
  }
  return normalizeFormDataDateForInput(raw);
};

const getCpcfaImportBlobAsRecord = (blob: unknown): Record<string, unknown> | null => {
  if (blob == null) return null;
  if (typeof blob === 'string') {
    try {
      const parsed = JSON.parse(blob) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
      return null;
    } catch {
      return null;
    }
  }
  if (typeof blob !== 'object' || Array.isArray(blob)) return null;
  return blob as Record<string, unknown>;
};

const isBlankOverlayValue = (v: unknown): boolean =>
  v === undefined || v === null || (typeof v === 'string' && v.trim() === '');

const setOverlayIfMissing = (
  details: ApplicationDetails,
  key: string,
  value: unknown
): void => {
  if (isBlankOverlayValue(details[key]) && !isBlankOverlayValue(value)) {
    details[key] = value;
  }
};

const parseCpcfaYesNoBoolean = (v: unknown): boolean | null => {
  if (v == null || v === '') return null;
  const s = String(v).trim().toUpperCase();
  if (s === 'Y' || s === 'YES' || s === 'TRUE') return true;
  if (s === 'N' || s === 'NO' || s === 'FALSE') return false;
  return null;
};

/** LOA_SEDI_* cells that contain street addresses (vs Y/N). Mirrors server `looksLikeSediAddressText`. */
const looksLikeCpcfaAddressText = (v: unknown): boolean => {
  if (v == null || v === '') return false;
  const s = String(v).trim();
  if (s.length < 8) return false;
  if (/^(Y|N|YES|NO|TRUE|FALSE|0|1|F|T|N\/A|NA)$/i.test(s)) return false;
  return (
    /\d/.test(s) &&
    (/,/.test(s) ||
      /\b(?:st|street|ave|avenue|rd|road|ct|court|dr|drive|ln|lane|blvd|way)\b/i.test(s) ||
      /\b[A-Z]{2}\s+\d{5}(-\d{4})?\b/i.test(s))
  );
};

/** FRED LOA_SEV_AFFECT_COM: these codes imply Severely Affected (per CAAT spec). */
const SAC_CODES_SEVERE_COMMUNITY = new Set(['EZ', 'HU', 'DA', 'CV']);

export const sacReasonImpliesSeverelyAffected = (raw: unknown): boolean => {
  const code = String(raw ?? '')
    .trim()
    .toUpperCase()
    .slice(0, 2);
  return SAC_CODES_SEVERE_COMMUNITY.has(code);
};

/** Column wins over the import blob and any formData copy of the same field. */
export function applyPersistedCspAppNumber(details: ApplicationDetails): void {
  const raw = details?.cspAppNumber;
  if (raw === null || raw === undefined || raw === '') return;
  const match = String(raw).match(/(\d+)/);
  if (!match) return;
  const n = Number.parseInt(match[1], 10);
  if (!Number.isInteger(n) || n < 1) return;
  details.formData_cspAppNumber = String(n);
}

export const applyCpcfaBusinessDataOverlay = (details: ApplicationDetails): void => {
  const blob = getCpcfaImportBlobAsRecord(details.cpcfaImportBlob);
  if (!blob) return;

  setOverlayIfMissing(details, 'businessData_naicsCode', blob.LOA_NAICS_CODE);
  setOverlayIfMissing(details, 'businessData_censusTractNumber', blob.LOA_CENSUS_TRACT_NUM);
  setOverlayIfMissing(
    details,
    'formData_averageAnnualRevenueLast3Years',
    parseNumericFromFormDataLeaf(blob.LOA_ANNUAL_REV)
  );
  setOverlayIfMissing(
    details,
    'businessData_annualRevenue',
    parseNumericFromFormDataLeaf(blob.LOA_AVG_ANN_REV_LAST_FY)
  );
  setOverlayIfMissing(details, 'businessData_numberOfFtes', parseNumericFromFormDataLeaf(blob.LOA_NUM_FTES));
  setOverlayIfMissing(
    details,
    'businessData_numberOfEmployees',
    parseNumericFromFormDataLeaf(blob.LOA_NBR_EMPOYEES)
  );
  setOverlayIfMissing(details, 'businessData_numberOfJobs', parseNumericFromFormDataLeaf(blob.LOA_NBR_JOBS));
  setOverlayIfMissing(details, 'businessData_county', blob.LOA_USED_COUNTY);
  setOverlayIfMissing(details, 'businessData_city', blob.LOA_USED_CITY);
  setOverlayIfMissing(details, 'businessData_zipCode', blob.LOA_USED_ZIP);
  setOverlayIfMissing(
    details,
    'businessData_addressLine1',
    blob.LOA_USED_ADDRESS ?? blob.LOA_USED_AT_ADDRESS
  );

  const opened2Years = parseCpcfaYesNoBoolean(blob.LOA_BUS_OPND_2YEARS);
  if (opened2Years !== null) {
    setOverlayIfMissing(details, 'businessData_businessOpened2Years', opened2Years);
  }
  const severeAffected = parseCpcfaYesNoBoolean(blob.LOA_SEV_AFFECTED);
  if (severeAffected !== null) {
    setOverlayIfMissing(details, 'businessData_severelyAffected', severeAffected);
  }
  setOverlayIfMissing(details, 'formData_sacReason', blob.LOA_SEV_AFFECT_COM);
  if (sacReasonImpliesSeverelyAffected(blob.LOA_SEV_AFFECT_COM)) {
    details.businessData_severelyAffected = true;
  }

  /** CPCFA `LOA_CALCAP_PCT` (e.g. 100) = % of loan enrolled in CalCAP → `borrowerEquity`. Not borrower fee; see `LOA_BORROWER_PREMIUM_AMNT`. */
  const enrolledPctFromBlob = parseNumericFromFormDataLeaf(blob.LOA_CALCAP_PCT);
  if (enrolledPctFromBlob !== null) {
    setOverlayIfMissing(details, 'borrowerEquity', enrolledPctFromBlob);
  }

  const loanStartIso = cpcfaBlobDateToIsoInput(blob.LOA_LENDER_LOAN_DT);
  if (loanStartIso) {
    setOverlayIfMissing(details, 'formData_loanStartDate', loanStartIso);
  }

  if (blob.LOA_INT_RATE_TYPE != null && String(blob.LOA_INT_RATE_TYPE).trim() !== '') {
    const s = String(blob.LOA_INT_RATE_TYPE).trim().toUpperCase();
    const c = s.charAt(0);
    const norm = c === 'F' || c === 'V' ? c : s.startsWith('FIX') ? 'F' : s.startsWith('VAR') ? 'V' : '';
    if (norm) setOverlayIfMissing(details, 'formData_interestRateType', norm);
  }
  const usedAtAddr = parseCpcfaYesNoBoolean(blob.LOA_USED_AT_ADDRESS);
  if (usedAtAddr !== null) {
    setOverlayIfMissing(details, 'formData_usedAtAddress', usedAtAddr ? 'Yes' : 'No');
  }
  setOverlayIfMissing(details, 'formData_calcapLoanNumPrior', blob.LOA_CALCAP_LOAN_NUM_PRIOR);
  setOverlayIfMissing(
    details,
    'formData_remainBalanceRestructure',
    parseNumericFromFormDataLeaf(blob.LOA_REMAIN_BAL)
  );
  setOverlayIfMissing(
    details,
    'formData_amountIncreaseRequested',
    parseNumericFromFormDataLeaf(blob.LOA_INCREASE)
  );
  setOverlayIfMissing(details, 'formData_cspAppNumber', blob.LOA_CSP_APPNUM);

  const loaSediOwnedCdfi = blob.LOA_SEDI_BUS_OWNED_CDFI;
  if (looksLikeCpcfaAddressText(loaSediOwnedCdfi)) {
    setOverlayIfMissing(details, 'formData_sediBusinessOwnedAddress', String(loaSediOwnedCdfi).trim());
    setOverlayIfMissing(details, 'sedi_businessOwnedCDFI', true);
  } else {
    const ownedYn = parseCpcfaYesNoBoolean(loaSediOwnedCdfi);
    if (ownedYn !== null) setOverlayIfMissing(details, 'sedi_businessOwnedCDFI', ownedYn);
  }

  const loaSediBuildArea = blob.LOA_SEDI_BUS_BUILD_AREA;
  if (looksLikeCpcfaAddressText(loaSediBuildArea)) {
    setOverlayIfMissing(details, 'formData_sediBusinessBuildAddress', String(loaSediBuildArea).trim());
    setOverlayIfMissing(details, 'sedi_businessBuildArea', true);
  } else {
    const buildAreaYn = parseCpcfaYesNoBoolean(loaSediBuildArea);
    if (buildAreaYn !== null) setOverlayIfMissing(details, 'sedi_businessBuildArea', buildAreaYn);
  }
};

const normalizeBusinessAddressJson = (raw: unknown): Record<string, unknown> | null => {
  if (raw == null) return null;
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * When flat DB columns are empty but Business.address has a single-line geocoder string
 * (e.g. "221 South Long Beach Boulevard, Compton, CA 90221"), derive parts for Business Information.
 */
const parseUsFormattedAddressForFallback = (
  formatted: string
): { line1?: string; city?: string; zip?: string } => {
  const trimmed = formatted.trim();
  if (!trimmed) return {};

  const parts = trimmed
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return {};
  if (parts.length === 1) return { line1: parts[0] };

  const last = parts[parts.length - 1];
  const stateZipMatch = last.match(/^([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
  if (stateZipMatch && parts.length >= 3) {
    return {
      line1: parts[0],
      city: parts.slice(1, -1).join(', '),
      zip: stateZipMatch[2],
    };
  }

  if (parts.length === 2) {
    const m = parts[1].match(/^(.+?)\s+([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
    if (m) {
      return { line1: parts[0], city: m[1].trim(), zip: m[3] };
    }
    return { line1: parts[0], city: parts[1] };
  }

  return { line1: parts[0], city: parts[1] };
};

/**
 * When ApplicationBusinessData address fields are empty (typical for CPCFA import), use the linked Business row.
 * Also fills from Business.address Json (autocomplete / geocoder) when addressLine1, city, zip are blank.
 */
export const applyBusinessTableAddressFallback = (details: ApplicationDetails): void => {
  const b = details.business;
  if (!b || typeof b !== 'object') return;

  const str = (v: unknown): string | undefined => {
    if (v === null || v === undefined) return undefined;
    const s = String(v).trim();
    return s === '' ? undefined : s;
  };

  let line1 = str((b as { addressLine1?: unknown }).addressLine1);
  let city = str((b as { city?: unknown }).city);
  let zip = str((b as { zipCode?: unknown }).zipCode);
  let county = str((b as { county?: unknown }).county);

  const addrObj = normalizeBusinessAddressJson((b as { address?: unknown }).address);
  if (addrObj) {
    if (!line1) line1 = str(addrObj.addressLine);
    if (!city) city = str(addrObj.locality);
    if (!zip) zip = str(addrObj.postalCode);
    if (!county) county = str(addrObj.adminDistrict2);
  }

  const formatted =
    str((b as { formattedAddress?: unknown }).formattedAddress) ||
    (addrObj ? str(addrObj.formattedAddress) : undefined);

  if (formatted && (!line1 || !city || !zip)) {
    const parsed = parseUsFormattedAddressForFallback(formatted);
    if (!line1 && parsed.line1) line1 = parsed.line1;
    if (!city && parsed.city) city = parsed.city;
    if (!zip && parsed.zip) zip = parsed.zip;
  }

  setOverlayIfMissing(details, 'businessData_addressLine1', line1);
  setOverlayIfMissing(details, 'businessData_city', city);
  setOverlayIfMissing(details, 'businessData_zipCode', zip);
  setOverlayIfMissing(details, 'businessData_county', county);
  setOverlayIfMissing(details, 'formData_businessCounty', county);
};

/** Copy shared Business profile fields onto the flat details keys so the tab can edit and compare them. */
export const hydrateSharedBusinessDisplayFields = (details: ApplicationDetails): void => {
  const business = details.business;
  const record = business && typeof business === 'object' ? business : {};
  const asText = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    return String(value);
  };
  const year = record.yearBusinessOpened;
  details[DISPLAY_BORROWER_NAME_KEY] = asText(record.contactName);
  details[DISPLAY_BUSINESS_DBA_KEY] = asText(record.name);
  details[DISPLAY_ADDRESS_LINE2_KEY] = asText(record.addressLine2);
  details[DISPLAY_BUSINESS_START_YEAR_KEY] =
    year === null || year === undefined || year === '' || Number.isNaN(Number(year))
      ? ''
      : String(year);
};

export const applyFormDataProcessingOverlay = (details: ApplicationDetails): void => {
  const fd = getFormDataAsRecord(details.formData);
  if (!fd) return;

  const businessOrgType = getFirstFormDataValue(fd, [
    'borrowerinformation_business_org_type',
    'borrowerInformation_businessOrgType',
    'businessOrgType',
  ]);
  if (businessOrgType !== undefined) details.formData_businessOrgType = String(businessOrgType);

  const businessCounty = getFirstFormDataValue(fd, [
    'borrowerinformation_business_county',
    'borrowerInformation_county',
    'businessCounty',
  ]);
  if (businessCounty !== undefined) details.formData_businessCounty = String(businessCounty);

  const avgAnnualRevenueLast3Years = getFirstFormDataValue(fd, [
    'borrowerinformation_annual_revenue_last_3year',
    'borrowerInformation_averageAnnualRevenueLastThreeYears',
    'averageAnnualRevenueLastThreeYears',
  ]);
  const avgAnnualRevenueNum = parseNumericFromFormDataLeaf(avgAnnualRevenueLast3Years);
  if (avgAnnualRevenueNum !== null) {
    details.formData_averageAnnualRevenueLast3Years = avgAnnualRevenueNum;
  }

  const numberOfFtes = getFirstFormDataValue(fd, [
    'borrowerinformation_num_fte',
    'borrowerInformation_num_fte',
    'borrowerinformation_number_of_ftes',
    'borrowerInformation_numberOfFtes',
  ]);
  const numberOfFtesNum = parseNumericFromFormDataLeaf(numberOfFtes);
  if (numberOfFtesNum !== null) {
    details.businessData_numberOfFtes = numberOfFtesNum;
  }

  const inSacOrSedi = getFirstFormDataValue(fd, [
    'borrowerinformation_in_sac_or_sedi',
    'inSacOrSedi',
  ]);
  if (inSacOrSedi !== undefined) details.formData_inSacOrSedi = String(inSacOrSedi);

  const sacReason = getFirstFormDataValue(fd, [
    'borrowerinformation_sac_reason',
    'sacReason',
  ]);
  if (sacReason !== undefined) details.formData_sacReason = String(sacReason);

  const loanStartDate = getFirstFormDataValue(fd, [
    'loaninformation_loan_start_date',
    'loanInformation_dateOfFirstDisbursement',
    'loaninformation_date_of_first_disbursement',
  ]);
  const normalizedLoanStartDate = normalizeFormDataDateForInput(loanStartDate);
  if (normalizedLoanStartDate) details.formData_loanStartDate = normalizedLoanStartDate;

  const interestRateType = getFirstFormDataValue(fd, [
    'loaninformation_interest_rate_type',
    'loanInformation_interestRateType',
  ]);
  if (interestRateType !== undefined) {
    const s = String(interestRateType).trim().toUpperCase();
    const c = s.charAt(0);
    details.formData_interestRateType = c === 'F' || c === 'V' ? c : s.startsWith('FIX') ? 'F' : s.startsWith('VAR') ? 'V' : c;
  }

  const usedAtAddressFd = getFirstFormDataValue(fd, [
    'loaninformation_used_at_address',
    'loanInformation_usedAtAddress',
  ]);
  if (usedAtAddressFd !== undefined) {
    details.formData_usedAtAddress = String(usedAtAddressFd);
  }

  const calcapLoanPrior = getFirstFormDataValue(fd, [
    'loaninformation_calcap_loan_num_prior',
    'loanInformation_calcapLoanNumPrior',
  ]);
  if (calcapLoanPrior !== undefined) {
    details.formData_calcapLoanNumPrior = String(calcapLoanPrior);
  }

  const remainBalFd = getFirstFormDataValue(fd, [
    'loaninformation_remain_bal',
    'loanInformation_remainBalanceRestructure',
  ]);
  const remainNum = parseNumericFromFormDataLeaf(remainBalFd);
  if (remainNum !== null) details.formData_remainBalanceRestructure = remainNum;

  const increaseFd = getFirstFormDataValue(fd, [
    'loaninformation_increase',
    'loanInformation_amountIncreaseRequested',
  ]);
  const incNum = parseNumericFromFormDataLeaf(increaseFd);
  if (incNum !== null) details.formData_amountIncreaseRequested = incNum;

  const cspAppFd = getFirstFormDataValue(fd, [
    'loaninformation_csp_appnum',
    'loanInformation_cspAppNumber',
  ]);
  if (cspAppFd !== undefined) details.formData_cspAppNumber = String(cspAppFd);

  const lenderContactFd = getFirstFormDataValue(fd, [
    'lender_contact_id',
    'lenderContactId',
    'application_lender_contact_id',
  ]);
  if (lenderContactFd !== undefined && lenderContactFd !== null && lenderContactFd !== '') {
    const n = Number(lenderContactFd);
    details.formData_lenderContactId = Number.isNaN(n) ? String(lenderContactFd) : n;
  }

  const sediCirc = getFirstFormDataValue(fd, [
    'sedi_circumstances',
    'borrowerinformation_sedi_circumstances',
    'ccapsediownedbusiness_sedi_circumstances',
  ]);
  if (sediCirc !== undefined) details.formData_sediCircumstances = String(sediCirc);

  // Do not overlay from supplementalsaccertification_* (Jul 23: SAC blank on OCR intake).
  const sediOwnedAddr = getFirstFormDataValue(fd, [
    'sedi_business_owned_address',
    'borrowerinformation_sedi_business_owned_address',
  ]);
  if (sediOwnedAddr !== undefined) details.formData_sediBusinessOwnedAddress = String(sediOwnedAddr);

  const sediBuildAddr = getFirstFormDataValue(fd, [
    'sedi_business_build_address',
    'borrowerinformation_sedi_business_build_address',
    'ccapsediownedbusiness_business_address_in_cdfi_investment_area',
  ]);
  if (sediBuildAddr !== undefined) details.formData_sediBusinessBuildAddress = String(sediBuildAddr);

  const cdfiInvAddr = getFirstFormDataValue(fd, [
    'cdfi_investment_area_address',
    'borrowerinformation_cdfi_investment_area_address',
    'sac_cdfi_investment_area_address',
  ]);
  if (cdfiInvAddr !== undefined) details.formData_cdfiInvestmentAreaAddress = String(cdfiInvAddr);

  const sacQual = getFirstFormDataValue(fd, ['sac_qualifying_event', 'borrowerinformation_sac_qualifying_event']);
  if (sacQual !== undefined) details.formData_sacQualifyingEvent = String(sacQual);
};

type OcrProcessingOverlayRow = {
  applicationField:
    | 'formData_businessOrgType'
    | 'formData_businessCounty'
    | 'formData_averageAnnualRevenueLast3Years'
    | 'formData_inSacOrSedi'
    | 'formData_sacReason'
    | 'formData_loanStartDate'
    | 'formData_interestRateType'
    | 'formData_usedAtAddress'
    | 'formData_calcapLoanNumPrior'
    | 'formData_remainBalanceRestructure'
    | 'formData_amountIncreaseRequested'
    | 'formData_cspAppNumber'
    | 'formData_lenderContactId'
    | 'formData_sediCircumstances'
    | 'formData_sediBusinessOwnedAddress'
    | 'formData_sediBusinessBuildAddress'
    | 'formData_cdfiInvestmentAreaAddress'
    | 'formData_sacQualifyingEvent';
  formDataKeys: string[];
  valueKind: 'string' | 'currency' | 'date' | 'yesNo';
};

const OCR_PROCESSING_OVERLAY_TO_FORMDATA: OcrProcessingOverlayRow[] = [
  {
    applicationField: 'formData_businessOrgType',
    formDataKeys: ['borrowerinformation_business_org_type', 'borrowerInformation_businessOrgType'],
    valueKind: 'string',
  },
  {
    applicationField: 'formData_businessCounty',
    formDataKeys: ['borrowerinformation_business_county', 'borrowerInformation_county'],
    valueKind: 'string',
  },
  {
    applicationField: 'formData_averageAnnualRevenueLast3Years',
    formDataKeys: [
      'borrowerinformation_annual_revenue_last_3year',
      'borrowerInformation_averageAnnualRevenueLastThreeYears',
    ],
    valueKind: 'currency',
  },
  {
    applicationField: 'formData_inSacOrSedi',
    formDataKeys: ['borrowerinformation_in_sac_or_sedi'],
    valueKind: 'yesNo',
  },
  {
    applicationField: 'formData_sacReason',
    formDataKeys: ['borrowerinformation_sac_reason'],
    valueKind: 'string',
  },
  {
    applicationField: 'formData_loanStartDate',
    formDataKeys: ['loaninformation_loan_start_date', 'loanInformation_dateOfFirstDisbursement'],
    valueKind: 'date',
  },
  {
    applicationField: 'formData_interestRateType',
    formDataKeys: ['loaninformation_interest_rate_type', 'loanInformation_interestRateType'],
    valueKind: 'string',
  },
  {
    applicationField: 'formData_usedAtAddress',
    formDataKeys: ['loaninformation_used_at_address', 'loanInformation_usedAtAddress'],
    valueKind: 'yesNo',
  },
  {
    applicationField: 'formData_calcapLoanNumPrior',
    formDataKeys: ['loaninformation_calcap_loan_num_prior', 'loanInformation_calcapLoanNumPrior'],
    valueKind: 'string',
  },
  {
    applicationField: 'formData_remainBalanceRestructure',
    formDataKeys: ['loaninformation_remain_bal', 'loanInformation_remainBalanceRestructure'],
    valueKind: 'currency',
  },
  {
    applicationField: 'formData_amountIncreaseRequested',
    formDataKeys: ['loaninformation_increase', 'loanInformation_amountIncreaseRequested'],
    valueKind: 'currency',
  },
  {
    applicationField: 'formData_cspAppNumber',
    formDataKeys: ['loaninformation_csp_appnum', 'loanInformation_cspAppNumber'],
    valueKind: 'string',
  },
  {
    applicationField: 'formData_lenderContactId',
    formDataKeys: ['lender_contact_id', 'lenderContactId', 'application_lender_contact_id'],
    valueKind: 'string',
  },
  {
    applicationField: 'formData_sediCircumstances',
    formDataKeys: [
      'sedi_circumstances',
      'borrowerinformation_sedi_circumstances',
      'ccapsediownedbusiness_sedi_circumstances',
    ],
    valueKind: 'string',
  },
  {
    applicationField: 'formData_sediBusinessOwnedAddress',
    formDataKeys: [
      'sedi_business_owned_address',
      'borrowerinformation_sedi_business_owned_address',
    ],
    valueKind: 'string',
  },
  {
    applicationField: 'formData_sediBusinessBuildAddress',
    formDataKeys: [
      'sedi_business_build_address',
      'borrowerinformation_sedi_business_build_address',
      'ccapsediownedbusiness_business_address_in_cdfi_investment_area',
    ],
    valueKind: 'string',
  },
  {
    applicationField: 'formData_cdfiInvestmentAreaAddress',
    formDataKeys: [
      'cdfi_investment_area_address',
      'borrowerinformation_cdfi_investment_area_address',
      'sac_cdfi_investment_area_address',
    ],
    valueKind: 'string',
  },
  {
    applicationField: 'formData_sacQualifyingEvent',
    formDataKeys: ['sac_qualifying_event', 'borrowerinformation_sac_qualifying_event'],
    valueKind: 'string',
  },
];

export const applyCalcapOcrFinancialOverlay = (details: ApplicationDetails): void => {
  const fd = getFormDataAsRecord(details.formData);
  if (!fd) return;

  for (const row of CALCAP_OCR_FORMDATA_TO_APPLICATION_FINANCIAL) {
    const raw = getFirstFormDataValue(fd, row.formDataKeys);
    if (raw === undefined) continue;
    if (row.valueKind === 'currency') {
      const n = parseNumericFromFormDataLeaf(raw);
      if (n !== null) details[row.applicationField] = n;
    } else {
      const n = parseCalcapPercentPointsFromFormData(raw);
      if (n !== null) details[row.applicationField] = n;
    }
  }
};

const setFormDataLeafValue = (
  fd: Record<string, unknown>,
  key: string,
  value: unknown
): void => {
  const current = fd[key];
  if (Array.isArray(current) && current.length > 0 && typeof current[0] === 'object' && current[0] !== null) {
    const nextFirst = { ...(current[0] as Record<string, unknown>), Value: value };
    fd[key] = [nextFirst, ...current.slice(1)];
    return;
  }
  if (current && typeof current === 'object' && !Array.isArray(current) && 'value' in (current as Record<string, unknown>)) {
    fd[key] = { ...(current as Record<string, unknown>), value };
    return;
  }
  fd[key] = value;
};

export const buildUpdatedOcrCalcapFormData = (
  baseFormData: unknown,
  detailsToPersist: ApplicationDetails
): Record<string, unknown> => {
  const base = getFormDataAsRecord(baseFormData) ?? {};
  const next = { ...base };

  CALCAP_OCR_FORMDATA_TO_APPLICATION_FINANCIAL.forEach((row) => {
    const keyToWrite =
      row.formDataKeys.find((k) => Object.prototype.hasOwnProperty.call(next, k)) ??
      row.formDataKeys[0];
    const v = detailsToPersist[row.applicationField];
    if (v === undefined) return;
    setFormDataLeafValue(next, keyToWrite, v === null ? '' : v);
  });

  return next;
};

export const buildUpdatedOcrProcessingFormData = (
  baseFormData: unknown,
  detailsToPersist: ApplicationDetails
): Record<string, unknown> => {
  const base = getFormDataAsRecord(baseFormData) ?? {};
  const next = { ...base };

  OCR_PROCESSING_OVERLAY_TO_FORMDATA.forEach((row) => {
    if (row.applicationField === 'formData_cspAppNumber') return;
    const keyToWrite =
      row.formDataKeys.find((k) => Object.prototype.hasOwnProperty.call(next, k)) ??
      row.formDataKeys[0];
    const raw = detailsToPersist[row.applicationField];
    if (raw === undefined) return;

    if (raw === null || raw === '') {
      setFormDataLeafValue(next, keyToWrite, '');
      return;
    }

    if (row.valueKind === 'currency') {
      const n = Number(raw);
      setFormDataLeafValue(next, keyToWrite, Number.isNaN(n) ? String(raw) : n);
      return;
    }
    if (row.valueKind === 'date') {
      const normalized = normalizeFormDataDateForInput(raw);
      setFormDataLeafValue(next, keyToWrite, normalized ?? '');
      return;
    }
    if (row.valueKind === 'yesNo') {
      const s = String(raw).trim().toLowerCase();
      if (s === 'yes' || s === 'y' || s === 'true') setFormDataLeafValue(next, keyToWrite, 'Yes');
      else if (s === 'no' || s === 'n' || s === 'false') setFormDataLeafValue(next, keyToWrite, 'No');
      else setFormDataLeafValue(next, keyToWrite, String(raw));
      return;
    }
    setFormDataLeafValue(next, keyToWrite, String(raw));
  });

  return next;
};

/** Flat field for Application Details PATCH → `Lender.otherOrganizationId` (Lender CalCAP ID). */
export function hydrateLenderOtherOrganizationIdForDetailsTab(details: ApplicationDetails): void {
  const lenderRec = details.lender || details.fundingProgramMembership?.lender;
  if (!lenderRec || typeof lenderRec !== 'object') {
    details.lenderOtherOrganizationId = '';
    return;
  }
  const raw =
    lenderRec.otherOrganizationId || lenderRec.lenderNumber || lenderRec.regulatoryId || '';
  details.lenderOtherOrganizationId =
    raw == null || raw === '' ? '' : String(raw).trim();
}

/** Merge nested businessData / demographics / sedi onto the flat Application details shape used by the tab. */
export function flattenApplicationDetails(details: ApplicationDetails): ApplicationDetails {
  const flattenedDetails = { ...details };
  const loaIdValue = details?.cpcfaImportBlob?.LOA_ID;
  flattenedDetails.loaId =
    loaIdValue !== undefined && loaIdValue !== null ? String(loaIdValue) : null;
  if (details.businessData) {
    Object.entries(details.businessData).forEach(([key, value]) => {
      if (key !== 'id' && key !== 'applicationId' && key !== 'createdDate' && key !== 'updatedDate') {
        flattenedDetails[`businessData_${key}`] = value;
      }
    });
  }
  if (details.demographics) {
    Object.entries(details.demographics).forEach(([key, value]) => {
      if (key !== 'id' && key !== 'applicationId' && key !== 'createdDate' && key !== 'updatedDate') {
        flattenedDetails[`demographics_${key}`] = value;
      }
    });
  }
  if (details.sedi) {
    Object.entries(details.sedi).forEach(([key, value]) => {
      if (key !== 'id' && key !== 'applicationId' && key !== 'createdDate' && key !== 'updatedDate') {
        flattenedDetails[`sedi_${key}`] = value;
      }
    });
  }
  hydrateLenderOtherOrganizationIdForDetailsTab(flattenedDetails);
  return flattenedDetails;
}

export function orderIndex(field: string, order: string[]): number {
  const i = order.indexOf(field);
  return i === -1 ? 9999 : i;
}

/**
 * Loan Details wireframe (exclusive list + order). All other former "loan block" fields are categorized as `other`.
 */
export const LOAN_DETAILS_WIREFRAME_ORDER: string[] = [
  'loanPurpose',
  'loanPurposeOther',
  'loanPurposeSecondary',
  'loanPurposeSecondaryOther',
  'governmentGuaranteeProgram',
  'guaranteeProgramName',
  'otherProgramPercentage',
  'borrowerEquity',
  'lenderLoanNumber',
  DETAIL_LENDER_LOAN_NUMBER_KEY,
  'loanType',
  'loanTypeOther',
  'askedAmount',
  'approvedAmount',
  'interestRate',
  'formData_interestRateType',
  'loanAPR',
  'secured',
  'refinancing',
  'formData_calcapLoanNumPrior',
  'formData_remainBalanceRestructure',
  'formData_amountIncreaseRequested',
];

const LOAN_DETAILS_WIREFRAME_FIELDS = new Set(LOAN_DETAILS_WIREFRAME_ORDER);

/**
 * Premiums wireframe (exclusive): fee → borrower % → fee → lender % → origination.
 * CalCAP Combined Rate (%) and legacy premium amount live under Other Information.
 */
export const PREMIUMS_WIREFRAME_ORDER: string[] = [
  'calcapPercentage',
  'borrowerPremiumAmount',
  'calcapPremiumAmount',
  'calcap',
  'totalOriginationCharges',
];

const PREMIUMS_WIREFRAME_FIELDS = new Set(PREMIUMS_WIREFRAME_ORDER);

/**
 * Important Dates wireframe (exclusive). Other date-like fields fall through to `hidden` (not shown on this tab).
 * CPCFA-style mappings: Date of Loan ≈ LOA_LENDER_LOAN_DT (formData); maturity/received/disbursement from Application columns.
 */
export const IMPORTANT_DATES_WIREFRAME_ORDER: string[] = [
  'formData_loanStartDate',
  'maturityDate',
  'receivedDate',
  // committedDate: Jul 23 — remove from UI; keep in DB
  'approvedDate',
  'disbursementDate',
];

const IMPORTANT_DATES_WIREFRAME_FIELDS = new Set(IMPORTANT_DATES_WIREFRAME_ORDER);

/** Form fields persisted on `application.formData` for SEDI circumstances / addresses (not on ApplicationSEDI row). */
export const SEDI_FORMDATA_WIREFRAME_KEYS = [
  'formData_sediCircumstances',
  'formData_sediBusinessOwnedAddress',
  'formData_sediBusinessBuildAddress',
] as const;

/**
 * SEDI wireframe (Application Details grid):
 * Row 1 — Not certified, Owned credit, Circumstances.
 * Row 2 — Owned CDFI, Business owned address.
 * Row 3 — Business build (checkbox syncs build + build area), Business build address.
 */
export const SEDI_WIREFRAME_ORDER: string[] = [
  'sedi_businessNotCertified',
  'sedi_businessOwnedCredit',
  'formData_sediCircumstances',
  'sedi_businessOwnedCDFI',
  'formData_sediBusinessOwnedAddress',
  'sedi_businessBuild',
  'formData_sediBusinessBuildAddress',
];

const SEDI_FORMDATA_WIREFRAME_FIELDS = new Set<string>(SEDI_FORMDATA_WIREFRAME_KEYS);

/** SAC Supplemental (after SEDI): persisted on `application.formData`. */
export const SAC_SUPPLEMENTAL_WIREFRAME_ORDER: string[] = [
  'formData_cdfiInvestmentAreaAddress',
  'formData_sacQualifyingEvent',
];

const SAC_SUPPLEMENTAL_WIREFRAME_FIELDS = new Set(SAC_SUPPLEMENTAL_WIREFRAME_ORDER);

/**
 * Change in Terms (CIT) — Application scalars; shown only when status is APPROVED.
 * Order matches CPCFA wireframe (Received → Date → Purpose → Effective → Approve/Reject).
 */
export const CHANGE_IN_TERMS_WIREFRAME_ORDER: string[] = [
  'citReceived',
  'citReceivedDate',
  'citPurpose',
  'citChangeEffectiveDate',
  'citApproveReject',
];

const CHANGE_IN_TERMS_WIREFRAME_FIELDS = new Set(CHANGE_IN_TERMS_WIREFRAME_ORDER);

/** First "Other Information" block: CPCFA blob + audit dates. */
export const OTHER_INFORMATION_WIREFRAME_ORDER: string[] = [
  'cpcfaImportBlob',
  'createdDate',
  'updatedDate',
];

/** Second "Other Information" block (same section title in wireframe). */
export const OTHER_INFORMATION_CONTINUED_ORDER: string[] = ['technicalAssistanceId'];

const OTHER_INFORMATION_WIREFRAME_FIELDS = new Set(OTHER_INFORMATION_WIREFRAME_ORDER);
const OTHER_INFORMATION_CONTINUED_FIELDS = new Set(OTHER_INFORMATION_CONTINUED_ORDER);

/** Not shown on Application Details “Business Data as of this Application” (edited elsewhere or out of wireframe). */
const BUSINESS_DATA_HIDDEN_ON_DETAILS_TAB = new Set([
  'businessData_county',
  'businessData_countyCode',
  'businessData_ein',
  'businessData_numberOfJobs',
]);

export function getApplicationDetailsFieldCategory(
  field: string,
  programKind: ApplicationDetailsProgramKind = 'other'
): string {
  if (field === DETAIL_LENDER_LOAN_NUMBER_KEY) {
    return programKind === 'capitalAccess' ? 'hidden' : 'loanDetails';
  }
  if (BUSINESS_DATA_HIDDEN_ON_DETAILS_TAB.has(field)) return 'hidden';
  if (WIRE_FRAME_BIZDATA_EXTRA_KEYS.includes(field)) return 'businessData';
  if (BUSINESS_SECTION_BUSINESSDATA_KEYS.has(field)) return 'business';
  if (field.startsWith('businessData_')) return 'businessData';
  if (field.startsWith('demographics_')) return 'demographics';
  if (field.startsWith('sedi_')) return 'sedi';
  if (SEDI_FORMDATA_WIREFRAME_FIELDS.has(field)) return 'sedi';
  if (SAC_SUPPLEMENTAL_WIREFRAME_FIELDS.has(field)) return 'sacSupplemental';
  if (CHANGE_IN_TERMS_WIREFRAME_FIELDS.has(field)) return 'changeInTerms';

  if (field === 'calcapLoanNumber') {
    return programKind === 'capitalAccess' ? 'basic' : 'hidden';
  }
  if (field === 'formData_cspAppNumber') {
    return programKind === 'csp' ? 'basic' : 'hidden';
  }
  if (field === 'lenderLoanNumber') {
    return programKind === 'capitalAccess' ? 'loanDetails' : 'basic';
  }
  if (['id', 'status'].includes(field)) return 'basic';
  // Jul 23: remove Funding Program ID from Application Details; keep program name only.
  if (field === 'fundingProgramId') return 'hidden';
  if (field === 'programName') return 'program';
  // Jul 23: remove Committed Date from Important Dates (DB field preserved).
  if (field === 'committedDate') return 'hidden';
  if (
    [
      'lenderId',
      'formData_lenderContactId',
      DISPLAY_LENDER_NAME_KEY,
      DISPLAY_LENDER_CALCAP_ID_KEY,
      DISPLAY_LENDER_CONTACT_PHONE_KEY,
      DISPLAY_LENDER_CONTACT_EMAIL_KEY,
    ].includes(field)
  ) {
    return 'lender';
  }
  if (
    [
      'businessId',
      'businessEin',
      'formData_businessOrgType',
      'formData_businessCounty',
      DISPLAY_BORROWER_NAME_KEY,
      DISPLAY_BUSINESS_DBA_KEY,
      ACTION_BUSINESS_DETAILS_KEY,
      DISPLAY_ADDRESS_LINE2_KEY,
      DISPLAY_BUSINESS_START_YEAR_KEY,
    ].includes(field)
  ) {
    return 'business';
  }

  if (
    [
      'formData_averageAnnualRevenueLast3Years',
      'formData_inSacOrSedi',
      'formData_sacReason',
      'formData_usedAtAddress',
    ].includes(field)
  ) {
    return 'businessData';
  }

  if (PREMIUMS_WIREFRAME_FIELDS.has(field)) return 'premiums';

  if (LOAN_DETAILS_WIREFRAME_FIELDS.has(field)) return 'loanDetails';

  if (IMPORTANT_DATES_WIREFRAME_FIELDS.has(field)) return 'dates';

  if (['deliveryMethod', 'receivedById'].includes(field)) return 'delivery';

  // Jul 23: hide Status Flags for everyone; keep DB fields.
  if (
    [
      'isActive',
      'lenderUnread',
      'edoUnread',
      'submittedByEdo',
      'approvedOnCommit',
      'sediBusiness',
      'first500Calcap',
    ].includes(field)
  ) {
    return 'hidden';
  }

  if (OTHER_INFORMATION_WIREFRAME_FIELDS.has(field)) return 'other';
  if (OTHER_INFORMATION_CONTINUED_FIELDS.has(field)) return 'otherContinued';

  return 'hidden';
}

export function organizeApplicationDetailsFields(
  allFields: string[],
  programKind: ApplicationDetailsProgramKind = 'other'
): string[] {
  const BASIC_ORDER =
    programKind === 'capitalAccess'
      ? ['id', 'status', 'calcapLoanNumber']
      : programKind === 'csp'
        ? ['id', 'status', 'lenderLoanNumber', 'formData_cspAppNumber']
        : ['id', 'status', 'lenderLoanNumber'];
  const PROGRAM_ORDER = ['programName'];
  const LENDER_ORDER = [
    'lenderId',
    DISPLAY_LENDER_NAME_KEY,
    DISPLAY_LENDER_CALCAP_ID_KEY,
    'formData_lenderContactId',
    DISPLAY_LENDER_CONTACT_PHONE_KEY,
    DISPLAY_LENDER_CONTACT_EMAIL_KEY,
  ];
  const BUSINESS_ORDER = [
    'businessId',
    DISPLAY_BORROWER_NAME_KEY,
    DISPLAY_BUSINESS_DBA_KEY,
    ACTION_BUSINESS_DETAILS_KEY,
    'businessEin',
    'formData_businessOrgType',
    'businessData_addressLine1',
    DISPLAY_ADDRESS_LINE2_KEY,
    'businessData_city',
    'formData_businessCounty',
    'businessData_zipCode',
    DISPLAY_BUSINESS_START_YEAR_KEY,
  ];

  /**
   * "Business Data as of this Application" — wireframe section ends after demographics row (veteran).
   * CalCAP loan # and CSP App# are shown under Basic Information.
   */
  const BUSINESS_DATA_ORDER = [
    'businessData_annualRevenue',
    'formData_averageAnnualRevenueLast3Years',
    'businessData_numberOfEmployees',
    'businessData_numberOfFtes',
    'businessData_expectedJobsCreated',
    'businessData_expectedJobsRetained',
    'businessData_severelyAffected',
    'formData_sacReason',
    'formData_usedAtAddress',
    BIZDATA_LOAN_ADDRESS_LINE1_KEY,
    BIZDATA_LOAN_CITY_KEY,
    BIZDATA_LOAN_COUNTY_KEY,
    BIZDATA_LOAN_ZIP_KEY,
    'businessData_naicsCode',
    'businessData_censusTractNumber',
    'businessData_minorityOwned',
    'businessData_womenOwned',
    DISPLAY_BIZDATA_VETERAN_OWNED_KEY,
    'businessData_businessOpened2Years',
  ];

  const LOAN_DETAILS_ORDER = LOAN_DETAILS_WIREFRAME_ORDER;

  const PREMIUMS_ORDER = PREMIUMS_WIREFRAME_ORDER;

  const DATES_ORDER = IMPORTANT_DATES_WIREFRAME_ORDER;
  const SEDI_ORDER = SEDI_WIREFRAME_ORDER;
  const SAC_SUPPLEMENTAL_ORDER = SAC_SUPPLEMENTAL_WIREFRAME_ORDER;
  const CHANGE_IN_TERMS_ORDER = CHANGE_IN_TERMS_WIREFRAME_ORDER;
  const OTHER_ORDER = OTHER_INFORMATION_WIREFRAME_ORDER;
  const OTHER_CONTINUED_ORDER = OTHER_INFORMATION_CONTINUED_ORDER;

  const DEMOGRAPHICS_ORDER = [
    'demographics_ethnicity',
    'demographics_ancestry',
    'demographics_gender',
    'demographics_genderDescribe',
    'demographics_genderPreference',
    'demographics_orientation',
    'demographics_veteranOwned',
    'demographics_disabledVeteran',
    'demographics_race',
  ];

  const categorized: Record<string, string[]> = {};
  WIRE_FRAME_SECTION_ORDER.forEach((section) => {
    categorized[section] = [];
  });

  allFields.forEach((field) => {
    const cat = getApplicationDetailsFieldCategory(field, programKind);
    if (cat === 'hidden') return;
    const bucket = categorized[cat] ?? categorized.other;
    bucket.push(field);
  });

  if (
    programKind !== 'capitalAccess' &&
    !categorized.loanDetails.includes(DETAIL_LENDER_LOAN_NUMBER_KEY)
  ) {
    categorized.loanDetails.push(DETAIL_LENDER_LOAN_NUMBER_KEY);
  }

  WIRE_FRAME_LENDER_DISPLAY_KEYS.forEach((k) => {
    if (!categorized.lender.includes(k)) categorized.lender.push(k);
  });

  WIRE_FRAME_BUSINESS_EXTRA_KEYS.forEach((k) => {
    if (!categorized.business.includes(k)) categorized.business.push(k);
  });

  // Always show Funding Program name (ID field hidden per Jul 23).
  if (!categorized.program.includes('programName')) {
    categorized.program.push('programName');
  }

  WIRE_FRAME_BIZDATA_EXTRA_KEYS.forEach((k) => {
    if (!categorized.businessData.includes(k)) categorized.businessData.push(k);
  });

  IMPORTANT_DATES_WIREFRAME_ORDER.forEach((k) => {
    if (!categorized.dates.includes(k)) categorized.dates.push(k);
  });

  SEDI_WIREFRAME_ORDER.forEach((k) => {
    if (!categorized.sedi.includes(k)) categorized.sedi.push(k);
  });

  SAC_SUPPLEMENTAL_WIREFRAME_ORDER.forEach((k) => {
    if (!categorized.sacSupplemental.includes(k)) categorized.sacSupplemental.push(k);
  });

  // Change in Terms keys are included only when the caller adds them (Approved apps).

  OTHER_INFORMATION_WIREFRAME_ORDER.forEach((k) => {
    if (!categorized.other.includes(k)) categorized.other.push(k);
  });

  OTHER_INFORMATION_CONTINUED_ORDER.forEach((k) => {
    if (!categorized.otherContinued.includes(k)) categorized.otherContinued.push(k);
  });

  const sortWithOrder = (list: string[], order: string[]) =>
    Array.from(new Set(list)).sort(
      (a, b) => orderIndex(a, order) - orderIndex(b, order) || a.localeCompare(b)
    );

  const orderedFields: string[] = [];

  for (const section of WIRE_FRAME_SECTION_ORDER) {
    const group = categorized[section] || [];
    if (group.length === 0) continue;

    switch (section) {
      case 'basic':
        orderedFields.push(...sortWithOrder(group, BASIC_ORDER));
        break;
      case 'program':
        orderedFields.push(...sortWithOrder(group, PROGRAM_ORDER));
        break;
      case 'lender':
        orderedFields.push(...sortWithOrder(group, LENDER_ORDER));
        break;
      case 'business':
        orderedFields.push(...sortWithOrder(group, BUSINESS_ORDER));
        break;
      case 'businessData':
        orderedFields.push(...sortWithOrder(group, BUSINESS_DATA_ORDER));
        break;
      case 'loanDetails':
        orderedFields.push(...sortWithOrder(group, LOAN_DETAILS_ORDER));
        break;
      case 'premiums':
        orderedFields.push(...sortWithOrder(group, PREMIUMS_ORDER));
        break;
      case 'dates':
        orderedFields.push(...sortWithOrder(group, DATES_ORDER));
        break;
      case 'demographics':
        orderedFields.push(...sortWithOrder(group, DEMOGRAPHICS_ORDER));
        break;
      case 'sedi':
        orderedFields.push(...sortWithOrder(group, SEDI_ORDER));
        break;
      case 'sacSupplemental':
        orderedFields.push(...sortWithOrder(group, SAC_SUPPLEMENTAL_ORDER));
        break;
      case 'delivery':
        orderedFields.push(...sortWithOrder(group, ['deliveryMethod', 'receivedById']));
        break;
      case 'flags':
        orderedFields.push(...group.sort((a, b) => a.localeCompare(b)));
        break;
      case 'other':
        orderedFields.push(...sortWithOrder(group, OTHER_ORDER));
        break;
      case 'otherContinued':
        orderedFields.push(...sortWithOrder(group, OTHER_CONTINUED_ORDER));
        break;
      case 'changeInTerms':
        orderedFields.push(...sortWithOrder(group, CHANGE_IN_TERMS_ORDER));
        break;
      default:
        orderedFields.push(...group.sort((a, b) => a.localeCompare(b)));
    }
  }

  return orderedFields;
}
